<template>
  <form
    @submit.prevent="submit"
    class="event-asset-form"
  >
    <layout-section-header
      :title="$t('global_event_assets')"
      :has-trigger="false"
      class="mt-4"
    />

    <magic-container class="mb-4">
      <div>
        <FileUploadArea
          ref="appsUploadArea"
          :onUploadFile="onUploadAppsFile"
          :stepOneText="$t('upload_apps_image_description')"
          :stepOneLink="stepOneLink"
          :linkText="$t('here')"
          :stepOneButtonText="$t('i_have_the_image')"
          :stepOneHeader="$t('upload_apps_image_header')"
          :stepTwoText="$t('click_here_to_add_the_image')"
        />
      </div>

      <div>
        <FileUploadArea
          ref="desktopUploadArea"
          :onUploadFile="onUploadDesktopFile"
          :stepOneText="$t('upload_desktop_image_description')"
          :linkText="$t('here')"
          :stepOneButtonText="$t('i_have_the_image')"
          :stepOneHeader="$t('upload_desktop_image_header')"
          :stepTwoText="$t('click_here_to_add_the_image')"
        />
      </div>
    </magic-container>

    <magic-container>
      <v-text-field
        v-model="name"
        :label="$t('global_event_assets_name')"
        persistent-hint
        :hint="$t('global_event_assets_name_hint')"
        :error-messages="nameErrors"
        @blur="v$.name.$touch()"
      />

      <v-autocomplete
        v-model="idColour"
        :items="assetsColours"
        :label="$t('text_colour')"
        required
        item-title="name"
        item-value="id"
        :error-messages="idColourErrors"
        :hide-details="false"
        persistent-hint
        :hint="$t('text_colour_hint')"
        @blur="v$.idColour.$touch()"
      />
    </magic-container>

    <layout-section-header
      title="Linked platforms"
      :has-trigger="false"
      class="mt-8"
    />

    <!--item-value="name" is because item.name = name + (country.code).
    See more in computed -> events().
    Id can not be unique
    Name can not be unique
    Country code can not be unique
    But Name + Country code has to be a unique pair
    -->
    <v-autocomplete
      v-model="selectedEvents"
      class="mb-8"
      multiple
      :items="events"
      item-title="name"
      item-value="name"
      return-object
      :hide-details="true"
      :label="$t('select_local_event_name')"
      :loading="isLoadingEvents"
      @click="() => isEventFieldFocused = true"
    >
      <template #selection="{ attrs, item }">
        <!-- text-color="#000" -->
        <v-chip
          v-bind="attrs"
          rounded
          size="compact"
          class="pr-2 pl-4"
        >
          {{ item.raw.name }}

          <v-icon
            class="ml-2 mr-0"
            size="20"
            @click="removeEvent(item)"
          >
            close
          </v-icon>
        </v-chip>
      </template>
    </v-autocomplete>

    <div class="form-buttons mt-4">
      <v-btn
        variant="text"
        @click="cancel"
      >
        {{ $t('cancel') }}
      </v-btn>

      <v-btn
        color="primary"
        class="trigger"
        variant="flat"
        :loading="isLoading"
        @click="submit"
      >
        {{ submitLabel }}
      </v-btn>
    </div>
  </Form>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { mapGetters } from 'vuex';
import fetchIfNotInStoreMixin from '@/mixins/fetchIfNotInStore';
import { isNonEmptyArr, isNum } from '@/utils';
import FileUploadArea from '../sponsorships/FileUploadArea.vue';

export default {
  emits: ['saved'],
  components: {
    FileUploadArea,
  },
  mixins: [fetchIfNotInStoreMixin],
  data() {
    return {
      assetId: null,
      name: '',
      idColour: null,
      appsImage: null,
      desktopImage: null,
      isLoading: false,
      selectedEvents: [],
      isEventFieldFocused: false,
      isLoadingEvents: false,
    };
  },
  created() {
    if (this.assetsColours.length === 0) {
      this.$store.dispatch('events/getAssetsColors');
    }
    if (this.assetsImageTypes.length === 0) {
      this.$store.dispatch('events/getAssetsImageTypes');
    }
    this.getEvents();
  },
  computed: {
    ...mapGetters('events', ['assetsColours', 'assetsImageTypes']),
    ...mapGetters('pepper', ['events']),
    events() {
      return this.$store.getters['pepper/events'].map((event) => {
        return {
          ...event,
          id: null,
          core_event_id: event.id,
          name: `${event.name} (${event.country.code})`,
        };
      });
    },
    stepOneLink() {
      // TODO: replace with actual link
      return this.isEditModal ? '/events/assets' : '/events/assets/create';
    },
    submitLabel() {
      return this.isEditModal ? this.$t('edit_event_asset') : this.$t('create_event_asset');
    },
    isEditModal() {
      return this.$route.name === 'event-assets-details';
    },
    nameErrors() {
      return this.v$.name.$errors.map(error => {
        if (error.$validator === 'required') return this.$t('required');
        return error.$message;
      });
    },
    idColourErrors() {
      return this.v$.idColour.$errors.map(error => {
        if (error.$validator === 'required') return this.$t('required');
        return error.$message;
      });
    },
  },
  methods: {
    removeEvent(event) {
      const indexToRemove = this.selectedEvents.findIndex(e => e.core_event_id === event.core_event_id && e.country.code === event.country.code);
      this.selectedEvents.splice(indexToRemove, 1);
    },
    getEvents() {
      this.isLoadingEvents = true;
      this.$store.dispatch('pepper/getEvents')
        .catch(err => err)
        .finally(() => {
          this.isLoadingEvents = false;
        });
    },
    onUploadAppsFile(file) {
      this.appsImage = file.file;
    },
    onUploadDesktopFile(file) {
      this.desktopImage = file.file;
    },
    cancel() {
      this.$router.push({ name: 'event-assets', query: this.$route.query });
    },
    async submit() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      this.isLoading = true;

      const formData = new FormData();
      formData.append('name', this.name);
      formData.append('color', this.idColour);

      // send events array if events are selected
      this.selectedEvents.forEach((event, index) => {
        if (isNum(event.id)) {
          formData.append(`events[${index}][id]`, event.id);
        }
        formData.append(`events[${index}][core_event_id]`, event.core_event_id);
        formData.append(`events[${index}][country]`, event.country.code);
        formData.append(`events[${index}][name]`, event.name);
      });
      // send empty events array if no events are selected
      if (this.selectedEvents.length === 0) {
        formData.append('events', JSON.stringify([]));
      }

      if (this.desktopImage) {
        formData.append(`images[desktop_img]`, this.desktopImage);
      }

      if (this.appsImage) {
        formData.append(`images[mobile_img]`, this.appsImage);
      }

      try {
        const action = this.assetId ? 'updateAsset' : 'createAsset';
        await this.$store.dispatch(`events/${action}`, {
          id: this.assetId,
          formData,
        });

        this.$emit('saved');
        this.$router.push({ name: 'event-assets', query: this.$route.query });
      } catch (error) {
        console.error('Error creating asset:', error);
      } finally {
        this.isLoading = false;
      }
    },
    clear() {
      this.v$.$reset();
      this.name = '';
      this.idColour = null;
      this.appsImage = null;
      this.desktopImage = null;
    },
    onReceiveItem(data) {
      this.assetId = data.id;
      this.name = data.name;
      this.idColour = data.color.id;
      const appsImage = data.images.find(image => image.type === 2);
      const desktopImage = data.images.find(image => image.type === 1);
      this.selectedEvents = data.events;
      this.checkEventName();

      if (desktopImage) {
        this.setDesktopImage(desktopImage.url);
      }
      if (appsImage) {
        this.setAppsImage(appsImage.url);
      }
    },
    setDesktopImage(image) {
      if (!this.$refs?.desktopUploadArea) {
        setTimeout(() => this.setDesktopImage(image), 100);
        return;
      }
      this.$refs.desktopUploadArea.setImage(image);
    },
    setAppsImage(image) {
      if (!this.$refs?.appsUploadArea) {
        setTimeout(() => this.setAppsImage(image), 100);
        return;
      }
      this.$refs.appsUploadArea.setImage(image);
    },
    checkEventName() {
      if (isNonEmptyArr(this.selectedEvents) && isNonEmptyArr(this.events) && this.isEditModal) {
        this.selectedEvents = this.selectedEvents.map((event) => {
          const eventWithName = this.events.find(e => (e.core_event_id === event.core_event_id) && (e.country.code === event.country.code))
          return {
            ...event,
            name: eventWithName ? eventWithName.name : '',
          };
        });
      }
    }
  },
  watch: {
    '$route.params.id': {
      handler(nextVal) {
        if (nextVal) {
          this.fetchIfNotInStore(nextVal,'events', 'assets', 'getAssets')
            .then((data) => {
              this.clear();
              this.onReceiveItem(data);
            }, () => this.leaveMissingItem(nextVal));
        }
      },
      deep: true,
      immediate: true,
    },
    events() {
      this.checkEventName()
    },
  },
  setup () {
    return { v$: useVuelidate() }
  },
  validations() {
    return {
      name: { required },
      idColour: { required },
    };
  },
};
</script>

<style scoped>
.event-asset-form {
  background-color: rgb(var(--v-theme-surface));
  padding: 1.5rem 1rem;
}
</style>
